import { useCallback, useEffect, useState } from "react";
import type { UserDto } from "@chess/shared";

import { createSession, fetchMe, logout } from "./api.js";

export type SessionState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "signed-in"; user: UserDto };

export interface Session {
  state: SessionState;
  signIn: (username: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export function useSession(): Session {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  // On every load, ask the server who the cookie belongs to. This is the whole
  // "remember me" mechanism — there's nothing in localStorage to fall out of sync.
  useEffect(() => {
    let cancelled = false;
    void fetchMe()
      .then((user) => {
        if (cancelled) return;
        setState(user ? { status: "signed-in", user } : { status: "anonymous" });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "anonymous" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (username: string) => {
    const user = await createSession(username);
    setState({ status: "signed-in", user });
  }, []);

  const signOut = useCallback(async () => {
    await logout();
    setState({ status: "anonymous" });
  }, []);

  return { state, signIn, signOut };
}
